import { useEffect, useRef, useState } from 'react'

import { CheckIcon } from '@chakra-ui/icons'
import { Box, IconButton, Spinner, Text } from '@chakra-ui/react'

import {
  useInterview,
  useUpdateInterview,
} from '@/react-query/interview/interview'
import { useIsViewOnlyMember } from '@/react-query/team'
import { PencilSvg } from '@/views/assets/PencilSvg'
import { AutoResizeTextarea } from '@/views/components/AutoResizeTextarea/AutoResizeTextarea'
import Card from '@/views/components/Card'
import { MarkdownRenderer } from '@/views/components/MarkdownRenderer/MarkdownRenderer'
import { StarRating } from '@/views/components/StarRating/StarRating'
import { useAutoGenerateShortTranscript } from '@/views/screens/Interview/hooks/useAutoGenerateShortTranscript'

import { Toolbox } from './Toolbox'

export const ShortTranscript = () => {
  const interview = useInterview().data
  const updateInterview = useUpdateInterview()

  useAutoGenerateShortTranscript()

  const shortTranscriptRef = useRef<HTMLDivElement>(null)

  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [localShortTranscript, setLocalShortTranscript] = useState<
    string | undefined
  >(interview?.shortTranscript || '')

  const isViewOnlyUser = useIsViewOnlyMember()

  useEffect(() => {
    setLocalShortTranscript(interview?.shortTranscript || '')
  }, [interview?.shortTranscript])

  const isGenerating = !localShortTranscript && !!interview?.fullTranscript

  return (
    <div className="flex flex-col flex-grow-[2]">
      <Toolbox
        copyRef={shortTranscriptRef}
        isCopyDisabled={isEditing}
        isGenerating={isGenerating}
      />
      <Card className="flex flex-col flex-grow px-[2.5rem] pt-[1.25rem] pb-[1.75rem] mt-5">
        <Box ref={shortTranscriptRef} flexGrow={1}>
          <Box className="flex justify-between items-center mb-[0.75rem]">
            <Text fontWeight="bold" fontSize="1.25rem">
              Short transcript
            </Text>
            {!isViewOnlyUser && !isGenerating && (
              <Box className="flex gap-[0.6rem]">
                <IconButton
                  onClick={(e) => {
                    e.preventDefault()
                    if (isEditing) {
                      updateInterview.mutate({
                        shortTranscript: localShortTranscript,
                      })
                    }
                    setIsEditing((isEditing) => !isEditing)
                  }}
                  variant="brand-solid-gray"
                  icon={isEditing ? <CheckIcon /> : <PencilSvg />}
                  aria-label="Edit short transcript"
                />
              </Box>
            )}
          </Box>
          {/* Use display none/block to prevent scroll jump to the top of the page, when editing is triggered*/}
          <AutoResizeTextarea
            rounded={'1.25rem'}
            flexGrow={1}
            onChange={(event) => {
              event.preventDefault()
              setLocalShortTranscript(event.target.value)
            }}
            value={localShortTranscript}
            variant={'brand-solid-white'}
            display={isEditing ? 'block' : 'none'}
          />
          <Box flexGrow={1} display={isEditing ? 'none' : 'block'}>
            {isGenerating ? (
              <Box className="flex items-center gap-3 py-6 text-gray-500">
                <Spinner size="sm" />
                <Text>Generating short transcript…</Text>
              </Box>
            ) : (
              <MarkdownRenderer
                text={localShortTranscript}
                className="flex-grow"
              />
            )}
          </Box>
        </Box>
      </Card>
      <Box mt={6} className={'flex justify-between items-center'}>
        <StarRating
          ratingTypeText={'short transcript'}
          ratingValue={interview?.shortTranscriptQuality}
          isDisabled={isGenerating}
          setRatingValue={(value) => {
            updateInterview.mutate({
              shortTranscriptQuality: value,
            })
          }}
        />
      </Box>
    </div>
  )
}
